import { createContext, useContext, useEffect, useState } from 'react';
import { getMyPermissions } from '../api/permissionsApi';

const PermissionsContext = createContext({ permissions: [], loaded: false });

function getUserRoles() {
  try {
    const raw = localStorage.getItem('user');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed?.roles) && parsed.roles.length) {
        return parsed.roles.map((r) => String(r).toLowerCase());
      }
      if (parsed?.role) {
        return [String(parsed.role).toLowerCase()];
      }
    }

    const legacyRoles = localStorage.getItem('roles');
    if (legacyRoles) {
      const parsed = JSON.parse(legacyRoles);
      if (Array.isArray(parsed) && parsed.length) {
        return parsed.map((r) => String(r).toLowerCase());
      }
    }

    const legacyRole = localStorage.getItem('role');
    if (legacyRole) {
      return [String(legacyRole).toLowerCase()];
    }
  } catch (e) {
    // ignore parse errors
  }

  return [];
}

function expandPermissionAliases(permissionName) {
  const original = String(permissionName);
  const aliases = new Set([original, original.toLowerCase()]);
  const requested = original.toLowerCase();

  const actionMap = { edit: 'update', update: 'edit' };

  if (requested.includes('.')) {
    const [resource, action] = requested.split('.', 2);
    const mappedAction = actionMap[action] || action;
    const singular = resource.endsWith('s') ? resource.slice(0, -1) : resource;
    const plural = resource.endsWith('s') ? resource : `${resource}s`;

    [
      `${singular}.${action}`,
      `${plural}.${action}`,
      `${mappedAction}_${singular}`,
      `${mappedAction}_${plural}`,
      `${singular}.${mappedAction}`,
      `${plural}.${mappedAction}`,
    ].forEach((name) => aliases.add(name));
  } else if (requested.includes('_')) {
    const [action, ...rest] = requested.split('_');
    const resource = rest.join('_');
    const mappedAction = actionMap[action] || action;
    const singular = resource.endsWith('s') ? resource.slice(0, -1) : resource;
    const plural = resource.endsWith('s') ? resource : `${resource}s`;

    [
      `${resource}.${action}`,
      `${singular}.${action}`,
      `${plural}.${action}`,
      `${resource}.${mappedAction}`,
      `${singular}.${mappedAction}`,
      `${plural}.${mappedAction}`,
    ].forEach((name) => aliases.add(name));
  }

  return [...aliases];
}

function normalizePermissions(perms) {
  const out = new Set();
  for (const perm of perms) {
    for (const alias of expandPermissionAliases(perm)) {
      out.add(alias);
    }
  }
  return [...out];
}

/**
 * Loads RBAC permissions after login. Sanctum tokens are not JWTs, so roles
 * come from the login response stored in localStorage.
 */
export function PermissionsProvider({ children }) {
  const [permissions, setPermissions] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadPermissions() {
      const roles = getUserRoles();

      if (roles.includes('admin')) {
        if (!cancelled) {
          setPermissions(['__admin__']);
          setLoaded(true);
        }
        return;
      }

      try {
        const res = await getMyPermissions();
        const perms = res.data?.permissions || [];
        if (!cancelled) setPermissions(normalizePermissions(perms));
      } catch (err) {
        console.error('[PermissionsContext] failed to load permissions', err);
        if (!cancelled) setPermissions([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    }

    loadPermissions();
    const onFocus = () => loadPermissions();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  return (
    <PermissionsContext.Provider value={{ permissions, loaded }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  return useContext(PermissionsContext);
}

/**
 * Hook to check if current user has a specific permission like 'payments.edit'.
 * Admins always return true.
 */
export function useHasPerm(perm) {
  const { permissions } = usePermissions();
  if (permissions.includes('__admin__')) return true;
  const granted = new Set(permissions.map((item) => String(item).toLowerCase()));
  return expandPermissionAliases(perm).some((candidate) =>
    granted.has(String(candidate).toLowerCase())
  );
}
